import React from 'react';
import styled from 'styled-components';

import {
  BOARD_FRIDAY_UNLOCK_ARIA_LABEL,
  BOARD_FRIDAY_UNLOCK_LABEL,
} from '../board/boardProgressCopy';
import { FRESH_FIND_UNLOCK_ENTRY } from './freshFindUnlockFunnel';

/**
 * THE ONE FRESH-FINDS-FRIDAY SIGN-IN DOOR, AS A SHARED COMPONENT (THE-968).
 *
 * It lived inside `BoardProgressCard` through PR 6 because the board was its
 * only host. THE-968 call 3 pulls it out here so the next Friday surface
 * (task D) consumes the same button rather than drawing a second one that
 * drifts in label, accessible name, funnel entry or tap target. The board now
 * imports it; nothing about the rendered markup changed in the move.
 *
 * TWO BACKGROUNDS, TWO SIZES, ONE COMPONENT:
 *  - `onDark` is white-on-chocolate for the stages 1-3 band/sticker (spec:
 *    *"a white pill"*) and near-black-on-cream for the stage-4 card, whose
 *    pale wash a white pill would vanish into.
 *  - `size` is the band's small `sm` pill versus stage 4's `lg` — the mock's
 *    *"big near-black button"*. Same door either way: one component, one
 *    handler, one event, so the two can never report different funnel entries.
 *
 * `data-signin-entry` is the repo-wide sign-in-door marker that
 * `src/__tests__/signInAlwaysReachable.test.tsx` scans for, carrying the same
 * verbatim entry string the handler reports — see `FreshFindFridaysCard`'s
 * `SignIn` for the full reasoning, which applies here unchanged.
 *
 * THE VISIBLE LABEL AND THE ACCESSIBLE NAME ARE DELIBERATELY DIFFERENT
 * LENGTHS (THE-968 call 3). The visible text shortened to "🔒 Sign in"; the
 * `aria-label` stayed the whole sentence, because "Sign in" read out of
 * context does not say what it signs you in to. Pinned in
 * `boardProgressCopy.test.ts`.
 */
export const FridayUnlockPill = styled.button<{
  $onDark: boolean;
  $size: 'sm' | 'lg';
}>`
  flex: 0 0 auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  margin: 0;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  /* Written as flat interpolations rather than ones returning a nested
     template literal, deliberately: the style-contract tests in this repo read
     styled-component CSS as SOURCE TEXT and stop at the first backtick, so a
     nested template literal truncates the block and silently makes every
     assertion below it vacuous. (It did, on the first pass.) */
  /* THE-1086: the sm size was 4px 10px padding / 12px font, ~28px tall —
     "the smallest button on the board", per the PDF's own measurement. sm
     is this pill's EVERY non-lg host (board header tile, board toast, the
     People-page tag, the hub's inline band door), so the fix is here, not a
     per-host override: "I'd like it about 36px tall with about 16px on
     each side, everywhere it appears" (PDF, verbatim) names the People page
     and the hub alongside the board. An explicit height rather than
     padding-plus-line-height math pins the 36px exactly, the same technique
     BoardHeader's own V1DesktopUpdatePicksPill override already uses for
     its 36px pill. lg (the old BoardProgressCard stage-4 button, and the
     hub's standalone unlock card) is untouched — neither surface nor size
     is named in the ask. */
  height: ${({ $size }) => ($size === 'lg' ? 'auto' : '36px')};
  padding: ${({ $size }) => ($size === 'lg' ? '11px 20px' : '0 16px')};
  /* THE-1265: sm's text was a flat 12px, off THE-1185's one pill spec
     (theme.pill.text, 14px/600) — Kate's "the Sign in pill ... uses the
     standard's text size and weight," with the dark fill and lock icon
     unchanged. lg is untouched; its 15px isn't named by this ticket. No
     backticks in this comment — this block is read as CSS source text by
     this repo's style-contract tests, which stop at the first backtick. */
  font-size: ${({ $size, theme }) => ($size === 'lg' ? '15px' : theme.pill.text)};
  background: ${({ $onDark, theme }) => ($onDark ? '#ffffff' : theme.color.inkSoft)};
  color: ${({ $onDark, theme }) => ($onDark ? theme.color.inkSoft : '#ffffff')};

  /* A 44px-tall invisible hit area, without changing the visible size — the
     same ::after technique, and the same tap-target rule, as
     FreshFindFridaysCard's SignInLink. */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -6px;
    right: -6px;
    height: 44px;
    transform: translateY(-50%);
  }
`;

export interface FridayUnlockProps {
  /** Opens sign-in. THE HANDLER IS THE CONDITION on every host surface. */
  onUnlock: () => void;
  /** True on the chocolate band/sticker, false on stage 4's pale card. */
  onDark: boolean;
  /** `lg` is stage 4's big button; omitted is the band's small pill. */
  size?: 'sm' | 'lg';
  /**
   * The funnel/door entry string this surface reports, which is also the
   * `data-signin-entry` value — ONE prop feeding both, so the marker
   * `signInAlwaysReachable` reads and the entry the analytics record can never
   * disagree. Defaults to the board's `fresh_find_fridays`.
   */
  entry?: string;
  /**
   * The surface's own full accessible name. THE-970 adopts this pill on two
   * more surfaces and each KEEPS the accessible name it already had — the
   * People-page tag says what it unlocks ("Fresh Find Fridays"), the hub's
   * locked card names the person ("picks for Noah & Charlotte"). A single
   * shared sentence would have made both read wrong, so the name is the
   * caller's and only the affordance is shared.
   */
  ariaLabel?: string;
  /** The surface's existing `data-testid`, kept so adopting the pill does not
   *  rename the handle its tests and funnel checks already use. */
  testId?: string;
}

/**
 * The one Unlock door, on either background.
 *
 * THE VISIBLE LABEL IS THE COMPONENT'S, THE ACCESSIBLE NAME IS THE CALLER'S
 * (THE-970). `BOARD_FRIDAY_UNLOCK_LABEL` is not a prop: the whole point of the
 * shared pill is that every Friday sign-in door looks and reads the same at a
 * glance, so a caller that could pass its own visible text would be drawing the
 * second pill this component exists to prevent. `ariaLabel` is a prop for the
 * opposite reason — see `FridayUnlockProps.ariaLabel`.
 *
 * See `FridayUnlockPill` for the styling and the two-background rationale.
 */
export function FridayUnlock({
  onUnlock,
  onDark,
  size = 'sm',
  entry = FRESH_FIND_UNLOCK_ENTRY,
  ariaLabel = BOARD_FRIDAY_UNLOCK_ARIA_LABEL,
  testId = 'board-friday-unlock',
}: FridayUnlockProps) {
  return (
    <FridayUnlockPill
      type="button"
      $onDark={onDark}
      $size={size}
      data-signin-entry={entry}
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={onUnlock}
    >
      {BOARD_FRIDAY_UNLOCK_LABEL}
    </FridayUnlockPill>
  );
}
